import { ENGLISH, Language } from 'app/constants/enums/Language';
import React, { createContext, PureComponent, ReactNode, useContext } from 'react';

/*
 * This file provides contexts for target language and display language.
 *
 * Target language is the language that the content was written in. For
 * example, a lesson's language is its target language. This affects display
 * of transliterations and certain translations. It's primarily used to
 * filter out the lesson language from the translation edit controls.
 *
 * Display language is the language that translations will be displayed in.
 * This defaults to the user's locale, which is the language that the UI
 * is translated to. This may be overridden locally in the case of something
 * like the editor lesson preview.
 *
 * For editors though, prefer using GeneralEditorContext. That additionally
 * provides the translation mode context among other things.
 */

// Target Language
const TargetLanguageContext = createContext<Language>(ENGLISH);

interface IProps {
  children: ReactNode;
  language?: Language;
}

export function TargetLanguageProvider({ children, language = ENGLISH }: IProps) {
  return (
    <TargetLanguageContext.Provider value={language}>
      {children}
    </TargetLanguageContext.Provider>
  );
}

export const TargetLanguageConsumer = TargetLanguageContext.Consumer;
export const useTargetLanguage = () => useContext(TargetLanguageContext);

// Display Language
const DisplayLanguageContext = createContext<Language>(ENGLISH);
const DisplayLanguageOverrideContext = createContext<(language: Language) => void>(() => {});

interface IState {
  language?: Language;
}

export class OverridableDisplayLanguageProvider extends PureComponent<unknown, IState> {
  public constructor(props: unknown) {
    super(props);
    this.state = {
      language: undefined,
    };
  }

  public render() {
    return (
      <DisplayLanguageConsumer>{(language) =>
        <DisplayLanguageContext.Provider
          value={this.state.language || language || ENGLISH}
        >
          <DisplayLanguageOverrideContext.Provider value={this.overrideLanguage}>
            {this.props.children}
          </DisplayLanguageOverrideContext.Provider>
        </DisplayLanguageContext.Provider>
        }
      </DisplayLanguageConsumer>
    );
  }

  private overrideLanguage = (language: Language) => {
    this.setState({ language });
  };
}

export function DisplayLanguageProvider({ language, children }: IProps) {
  return (
    <DisplayLanguageContext.Provider value={language || ENGLISH}>
      {children}
    </DisplayLanguageContext.Provider>
  );
}

export const DisplayLanguageConsumer = DisplayLanguageContext.Consumer;
export const useDisplayLanguage = () => useContext(DisplayLanguageContext);
export const DisplayLanguageOverride = DisplayLanguageOverrideContext.Consumer;
export const useDisplayLanguageOverride = () => useContext(DisplayLanguageOverrideContext);
